import type { GameEvents } from './events';

type Handler<T> = (payload: T) => void;

export interface LoggedEvent {
  /** Simulation frame (fixed step index) at emit time. */
  frame: number;
  type: string;
  payload: unknown;
}

/**
 * Typed synchronous event bus. Handlers run immediately inside `emit`, in subscription order.
 * Subscribing returns an unsubscribe function — call it in your system's `dispose`.
 */
export class EventBus<M extends object = GameEvents> {
  private handlers = new Map<keyof M, Handler<unknown>[]>();
  private anyHandlers: ((type: keyof M, payload: unknown) => void)[] = [];
  /** Ring buffer of recent events (for debugging / window.__bh.events). */
  readonly log: LoggedEvent[] = [];
  logLimit = 500;
  /** Events excluded from the log (too chatty). */
  readonly logExclude = new Set<string>();
  /** Supplies the frame number stamped on logged events. */
  frameSource: () => number = () => 0;

  on<K extends keyof M>(type: K, handler: Handler<M[K]>): () => void {
    let list = this.handlers.get(type);
    if (!list) {
      list = [];
      this.handlers.set(type, list);
    }
    list.push(handler as Handler<unknown>);
    return () => this.off(type, handler);
  }

  once<K extends keyof M>(type: K, handler: Handler<M[K]>): () => void {
    const off = this.on(type, (p) => {
      off();
      handler(p);
    });
    return off;
  }

  off<K extends keyof M>(type: K, handler: Handler<M[K]>): void {
    const list = this.handlers.get(type);
    if (!list) return;
    const i = list.indexOf(handler as Handler<unknown>);
    if (i >= 0) list.splice(i, 1);
  }

  /** Listen to every event (debug tooling, recorders). */
  onAny(handler: (type: keyof M, payload: unknown) => void): () => void {
    this.anyHandlers.push(handler);
    return () => {
      const i = this.anyHandlers.indexOf(handler);
      if (i >= 0) this.anyHandlers.splice(i, 1);
    };
  }

  emit<K extends keyof M>(type: K, payload: M[K]): void {
    if (!this.logExclude.has(type as string)) {
      this.log.push({ frame: this.frameSource(), type: type as string, payload });
      if (this.log.length > this.logLimit) this.log.splice(0, this.log.length - this.logLimit);
    }
    const list = this.handlers.get(type);
    if (list) {
      // Copy so handlers may unsubscribe during dispatch.
      const snapshot = list.slice();
      for (const h of snapshot) h(payload);
    }
    for (const h of this.anyHandlers) h(type, payload);
  }

  clear(): void {
    this.handlers.clear();
    this.anyHandlers = [];
    this.log.length = 0;
  }
}
